import React, { useState, useEffect } from "react";
import { Container, Button, Alert } from "react-bootstrap";
import Loading from "../components/Loading";
import ModernSurveyCard from "../components/ModernSurveyCard";
import SurveyWithVariants from "../data/SurveyWithVariants";
import fetchData from "../helpFunctions/fetchData";
import SurveyConfig from "../components/SurveyConfig";
import { convertOutData } from "../helpFunctions/convertOutData";
import { useNavigate } from "react-router-dom";

const SurveyManagementScreen = () => {
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [alertMessage, setAlertMessage] = useState({
    type: "",
    header: "",
    msg: "",
  });
  const [serviceAccountEmail, setServiceAccountEmail] = useState("");
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configMode, setConfigMode] = useState("create"); // "create" or "update"

  const navigate = useNavigate();

  // extract the base front url
  let baseUrl = window.location.host;
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }

  useEffect(() => {
    // Check if user is authenticated before fetching data
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.log("No auth token found, redirecting to login");
      navigate("/login");
      return;
    }

    const fetchServiceAccount = async () => {
      const serviceAccount = await fetchData(
        navigate,
        "GET",
        setSurveys,
        setViewMode,
        setAlertMessage
      );
      console.log("🚀 ~ fetchServiceAccount ~ serviceAccount:", serviceAccount);
      setServiceAccountEmail(serviceAccount);
    };
    fetchServiceAccount();
  }, [navigate]);

  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (showConfigModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showConfigModal]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showConfigModal) {
        handleCloseModal();
      }
    };

    if (showConfigModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showConfigModal]);

  const handleSurveySelect = (index) => {
    setSelectedSurvey(index);
    setConfigMode("update");
    setShowConfigModal(true);
  };

  const handleCreateSurvey = () => {
    setSelectedSurvey(null);
    setConfigMode("create");
    setShowConfigModal(true);
  };

  const handleCloseModal = () => {
    setShowConfigModal(false);
    setSelectedSurvey(null);
    // Clear any alert messages when closing
    if (viewMode === "alert") {
      setViewMode("list");
    }
  };

  const handleSurveyChange = (surveyData) => {
    const method =
      configMode === "create" ? "POST" : isNaN(surveyData) ? "PUT" : "DELETE";
    console.log("🚀 ~ handleSurveyChange ~ method:", method);
    const body =
      method === "POST" || method === "PUT"
        ? JSON.stringify(convertOutData(surveyData))
        : "";
    console.log("🚀 ~ handleSurveyChange ~ body:", body);

    fetchData(
      navigate,
      method,
      setSurveys,
      setViewMode,
      setAlertMessage,
      body,
      surveyData,
      surveys
    );

    // Close modal after initiating save/delete
    // The alert will show the result
    setShowConfigModal(false);
    setSelectedSurvey(null);
  };

  const copyLinkToClipboard = (surveyId) => {
    const votersLink = `${baseUrl}survey/${surveyId}`;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(votersLink)
        .then(() => {
          setAlertMessage({
            type: "success",
            header: "Link copied to clipboard!",
          });
          setViewMode("alert");
        })
        .catch((error) => {
          console.error("Failed to copy link: ", error);
        });
    } else {
      copyTextToClipboard(votersLink);
    }
  };

  const copyTextToClipboard = (text) => {
    // Create a textarea element
    const textarea = document.createElement("textarea");
    textarea.value = text;

    // Set the textarea to be invisible
    textarea.style.position = "fixed";
    textarea.style.opacity = 0;

    // Append the textarea to the document body
    document.body.appendChild(textarea);

    // Select the text within the textarea
    textarea.select();

    try {
      // Copy the selected text to the clipboard
      document.execCommand("copy");
      alert("Text copied to clipboard:", text);
    } catch (error) {
      alert("Failed to copy text to clipboard:", error);
    } finally {
      // Remove the textarea from the document body
      document.body.removeChild(textarea);
    }
  };

  return (
    <Container className="my-4" style={{ maxWidth: "1400px" }}>
      <div className="survey-management-wrapper">
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between mb-4">
          <h1 className="survey-management-title mb-3 mb-md-0">Survey Management</h1>
          <div>
            {viewMode === "list" && (
              <Button className="btn-create-survey" onClick={handleCreateSurvey}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  style={{ marginRight: '8px', verticalAlign: 'text-bottom' }}
                >
                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                </svg>
                Create Survey
              </Button>
            )}
          </div>
        </div>

        {viewMode === "alert" && (
          <Alert
            variant={alertMessage.type}
            onClose={() => setViewMode("list")}
            dismissible
            className="survey-alert"
          >
            <Alert.Heading>{alertMessage.header}</Alert.Heading>
            <p>{alertMessage.msg}</p>
          </Alert>
        )}

                    {viewMode === "loading" && <Loading message="Loading Surveys" />}

        {viewMode === "list" && (
          <div>
            {surveys.length === 0 ? (
              <div className="text-center py-5 empty-state">
                <h3 className="mb-3">No surveys exist</h3>
                <Button className="btn-create-survey" onClick={handleCreateSurvey}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    style={{ marginRight: '8px', verticalAlign: 'text-bottom' }}
                  >
                    <path d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"/>
                  </svg>
                  Create Your First Survey
                </Button>
              </div>
            ) : (
              <div className="survey-grid">
                {surveys.map((survey, index) => (
                  <ModernSurveyCard
                    key={survey.surveyId || index}
                    survey={survey}
                    index={index}
                    onSelect={handleSurveySelect}
                    onCopyLink={copyLinkToClipboard}
                    totalVotes={survey.totalVotes || Math.floor(Math.random() * 100)}
                    targetVotes={100}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {showConfigModal && (
          <>
            {/* Modal Backdrop */}
            <div
              className="modal-backdrop-custom show"
              onClick={handleCloseModal}
            />

            {/* Modal Content */}
            <div className="modal-custom show">
              <div className="modal-custom-content">
                {/* Modal Header with Close Button */}
                <div className="modal-custom-header">
                  <h2 className="modal-custom-title">
                    {configMode === "create" ? "Create New Survey" : "Edit Survey"}
                  </h2>
                  <button
                    className="modal-close-btn"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                {/* Modal Body */}
                <div className="modal-custom-body">
                  <SurveyConfig
                    surveyData={
                      configMode === "update"
                        ? surveys[selectedSurvey]
                        : new SurveyWithVariants()
                    }
                    handleSurveyChange={handleSurveyChange}
                    serviceAccountEmail={serviceAccountEmail}
                    onClose={handleCloseModal}
                    mode={configMode}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default SurveyManagementScreen;
